import React from 'react';

const SKIN_COLORS = ['#FFD5A0', '#F5C5A3', '#E0A370', '#C68642', '#8D5524', '#6B3A1F'];
const HAIR_COLORS = ['#4A3728', '#1C1008', '#D4A56A', '#FF6B6B', '#A493FF', '#2D8CFF', '#38E54D', '#FFC03D'];
const EYE_COLORS = ['#4D96FF', '#38E54D', '#8B4513', '#1E293B', '#A493FF', '#FF6B6B'];
const ACCESSORIES = [
  { id: 'none', label: 'None' },
  { id: 'glasses', label: 'Glasses' },
  { id: 'cap', label: 'Cap' },
  { id: 'bow', label: 'Hair Bow' },
  { id: 'headband', label: 'Headband' },
  { id: 'crown', label: 'Crown' },
];

const AvatarSVG = ({ skin = '#FFD5A0', hair = '#4A3728', eyes = '#4D96FF', accessory = 'none', size = 120 }) => (
  <svg viewBox="0 0 120 120" width={size} height={size} className="drop-shadow-lg">
    {/* Background circle */}
    <circle cx="60" cy="60" r="58" fill={`${skin}33`} stroke={`${skin}66`} strokeWidth="2" />
    
    {/* Neck */}
    <rect x="48" y="82" width="24" height="14" rx="4" fill={skin} />
    
    {/* Face */}
    <ellipse cx="60" cy="58" rx="32" ry="35" fill={skin} />
    
    {/* Hair - back */}
    <ellipse cx="60" cy="38" rx="34" ry="24" fill={hair} />
    <rect x="26" y="30" width="68" height="18" rx="8" fill={hair} />
    
    {/* Hair sides */}
    <ellipse cx="30" cy="52" rx="6" ry="16" fill={hair} />
    <ellipse cx="90" cy="52" rx="6" ry="16" fill={hair} />
    
    {/* Ears */}
    <ellipse cx="28" cy="60" rx="5" ry="7" fill={skin} />
    <ellipse cx="92" cy="60" rx="5" ry="7" fill={skin} />
    
    {/* Eyes */}
    <ellipse cx="47" cy="58" rx="7" ry="7.5" fill="white" />
    <ellipse cx="73" cy="58" rx="7" ry="7.5" fill="white" />
    <circle cx="48" cy="58" r="4.5" fill={eyes} />
    <circle cx="74" cy="58" r="4.5" fill={eyes} />
    <circle cx="49.5" cy="56.5" r="1.5" fill="white" />
    <circle cx="75.5" cy="56.5" r="1.5" fill="white" />
    
    {/* Eyebrows */}
    <path d={`M39 50 Q47 46 55 50`} fill="none" stroke={hair} strokeWidth="2.5" strokeLinecap="round" />
    <path d={`M65 50 Q73 46 81 50`} fill="none" stroke={hair} strokeWidth="2.5" strokeLinecap="round" />
    
    {/* Nose */}
    <ellipse cx="60" cy="66" rx="3" ry="2.5" fill={`${skin}CC`} />
    
    {/* Mouth - smile */}
    <path d="M50 74 Q60 82 70 74" fill="none" stroke="#E07070" strokeWidth="2.5" strokeLinecap="round" />
    
    {/* Cheek blush */}
    <circle cx="40" cy="70" r="5" fill="#FF9999" opacity="0.3" />
    <circle cx="80" cy="70" r="5" fill="#FF9999" opacity="0.3" />
    
    {/* Accessories */}
    {accessory === 'glasses' && (
      <g>
        <circle cx="47" cy="58" r="10" fill="none" stroke="#1E293B" strokeWidth="2" />
        <circle cx="73" cy="58" r="10" fill="none" stroke="#1E293B" strokeWidth="2" />
        <line x1="57" y1="58" x2="63" y2="58" stroke="#1E293B" strokeWidth="2" />
        <line x1="37" y1="56" x2="28" y2="53" stroke="#1E293B" strokeWidth="2" />
        <line x1="83" y1="56" x2="92" y2="53" stroke="#1E293B" strokeWidth="2" />
      </g>
    )}
    {accessory === 'cap' && (
      <g>
        <ellipse cx="60" cy="34" rx="36" ry="12" fill="#4D96FF" />
        <rect x="24" y="28" width="72" height="10" rx="5" fill="#4D96FF" />
        <rect x="70" y="32" width="28" height="6" rx="3" fill="#3B7BE0" />
      </g>
    )}
    {accessory === 'bow' && (
      <g>
        <circle cx="82" cy="35" r="3" fill="#FF6B6B" />
        <ellipse cx="76" cy="35" rx="6" ry="4" fill="#FF6B6B" />
        <ellipse cx="88" cy="35" rx="6" ry="4" fill="#FF6B6B" />
      </g>
    )}
    {accessory === 'headband' && (
      <rect x="26" y="38" width="68" height="5" rx="2.5" fill="#FFC03D" />
    )}
    {accessory === 'crown' && (
      <g>
        <polygon points="38,32 42,18 50,28 60,14 70,28 78,18 82,32" fill="#FFC03D" stroke="#E6A820" strokeWidth="1.5" />
        <rect x="38" y="30" width="44" height="6" rx="2" fill="#FFC03D" stroke="#E6A820" strokeWidth="1" />
        <circle cx="50" cy="24" r="2" fill="#FF6B6B" />
        <circle cx="60" cy="18" r="2" fill="#4D96FF" />
        <circle cx="70" cy="24" r="2" fill="#38E54D" />
      </g>
    )}
    
    {/* Body/Shirt hint */}
    <path d="M32 96 Q60 88 88 96 L92 120 L28 120 Z" fill="#4D96FF" />
    <circle cx="60" cy="104" r="3" fill="white" />
  </svg>
);

const AvatarBuilder = ({ skin, hair, eyes, accessory, onChange, compact = false }) => {
  if (compact) {
    return (
      <div className="flex items-center gap-4">
        <AvatarSVG skin={skin} hair={hair} eyes={eyes} accessory={accessory} size={80} />
        <div className="flex gap-2 flex-wrap">
          {SKIN_COLORS.slice(0, 4).map(c => (
            <button key={c} onClick={() => onChange({ skin: c })} className={`w-7 h-7 rounded-full border-2 transition-all ${skin === c ? 'border-[#4D96FF] scale-125' : 'border-slate-200'}`} style={{ backgroundColor: c }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-center">
        <div className="relative">
          <AvatarSVG skin={skin} hair={hair} eyes={eyes} accessory={accessory} size={140} />
        </div>
      </div>

      {/* Skin Color */}
      <div>
        <label className="block text-sm font-bold text-[#1E293B] mb-2">Skin Tone</label>
        <div className="flex gap-2 flex-wrap">
          {SKIN_COLORS.map(c => (
            <button key={c} onClick={() => onChange({ skin: c })} data-testid={`skin-${c}`}
              className={`w-10 h-10 rounded-xl border-3 transition-all hover:scale-110 ${skin === c ? 'border-[#4D96FF] ring-4 ring-[#4D96FF]/20 scale-110' : 'border-slate-200'}`} style={{ backgroundColor: c }} />
          ))}
        </div>
      </div>

      {/* Hair Color */}
      <div>
        <label className="block text-sm font-bold text-[#1E293B] mb-2">Hair Color</label>
        <div className="flex gap-2 flex-wrap">
          {HAIR_COLORS.map(c => (
            <button key={c} onClick={() => onChange({ hair: c })} data-testid={`hair-${c}`}
              className={`w-10 h-10 rounded-xl border-3 transition-all hover:scale-110 ${hair === c ? 'border-[#4D96FF] ring-4 ring-[#4D96FF]/20 scale-110' : 'border-slate-200'}`} style={{ backgroundColor: c }} />
          ))}
        </div>
      </div>

      {/* Eye Color */}
      <div>
        <label className="block text-sm font-bold text-[#1E293B] mb-2">Eye Color</label>
        <div className="flex gap-2 flex-wrap">
          {EYE_COLORS.map(c => (
            <button key={c} onClick={() => onChange({ eyes: c })} data-testid={`eyes-${c}`}
              className={`w-10 h-10 rounded-xl border-3 transition-all hover:scale-110 ${eyes === c ? 'border-[#4D96FF] ring-4 ring-[#4D96FF]/20 scale-110' : 'border-slate-200'}`} style={{ backgroundColor: c }} />
          ))}
        </div>
      </div>

      {/* Accessory */}
      <div>
        <label className="block text-sm font-bold text-[#1E293B] mb-2">Accessory</label>
        <div className="flex gap-2 flex-wrap">
          {ACCESSORIES.map(a => (
            <button key={a.id} onClick={() => onChange({ accessory: a.id })} data-testid={`accessory-${a.id}`}
              className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${accessory === a.id ? 'bg-[#4D96FF] text-white' : 'bg-[#F4F8FB] text-[#64748B] border-2 border-slate-200 hover:border-[#4D96FF]'}`}>
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export { AvatarSVG, AvatarBuilder, SKIN_COLORS, HAIR_COLORS, EYE_COLORS, ACCESSORIES };
export default AvatarBuilder;
